import React, { createContext, useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import mealName from './mealName';
import { groceryListLabel } from './groceryListLabel';

/**
 * How a meal chip reports that it is hovered: `{ show(meal, element), showList(groceryList, element),
 * hide(), name(meal) }`, or null outside a month grid, where a chip shows nothing. `name` is what the
 * chip calls its meal (mealName). A day's grocery list pill reports itself through `showList`.
 */
export const MealTipContext = createContext(null);

const MAX_INGREDIENTS = 8;
const MAX_LIST_ITEMS = 12;

/**
 * A hovered meal's full name and what its meal idea knows about it, for a chip in the month grid, or
 * what a hovered grocery list pill holds.
 *
 * Drawn in a portal at fixed coordinates rather than beside the chip, because a day clips whatever
 * overflows it. One tooltip serves the whole grid: MonthGrid provides `context` to its chips and
 * renders `tooltip`. Nothing shows during a drag, and a scroll hides it, since its coordinates are
 * where the chip was.
 */
export function useMealChipTooltip(dragging, mealIdeasById) {
	const [tip, setTip] = useState(null);

	useEffect(() => {
		if(dragging) {
			setTip(null);
		}
	}, [dragging]);

	// A chip that goes, say when its meal is saved or moved, never gets the mouseleave that hides it.
	useEffect(() => {
		if(tip && !tip.element.isConnected) {
			setTip(null);
		}
	});

	useEffect(() => {
		if(!tip) {
			return undefined;
		}
		const hide = () => setTip(null);
		window.addEventListener('scroll', hide, true);
		return () => window.removeEventListener('scroll', hide, true);
	}, [tip]);

	const context = useMemo(() => ({
		show: (meal, element) => {
			if(!dragging) {
				setTip({ meal, element, rect: element.getBoundingClientRect() });
			}
		},
		showList: (groceryList, element) => {
			if(!dragging) {
				setTip({ groceryList, element, rect: element.getBoundingClientRect() });
			}
		},
		hide: () => setTip(null),
		name: (meal) => mealName(meal, ideaOf(meal)),
	}), [dragging, mealIdeasById]);

	function ideaOf(meal) {
		return meal.meal_idea_id && mealIdeasById ? mealIdeasById[Number(meal.meal_idea_id)] || null : null;
	}

	const tooltip = tip && ReactDOM.createPortal(
		tip.groceryList
			? <GroceryListTooltip groceryList={tip.groceryList} rect={tip.rect} />
			: <MealChipTooltip meal={tip.meal} idea={ideaOf(tip.meal)} rect={tip.rect} />,
		document.body
	);

	return { context, tooltip };
}

/**
 * Starts at the hovered element's left edge, above it when it is in the lower half of the window and
 * below it otherwise, so a tall tooltip has room. Kept inside the window's width.
 */
function tipStyle(rect) {
	const margin = 8;
	const width = 280;
	const above = rect.top > window.innerHeight / 2;
	return {
		left: Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin)),
		...(above ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
	};
}

export function MealChipTooltip({ meal, idea, rect }) {
	const style = tipStyle(rect);

	const ingredients = idea ? idea.ingredients || [] : [];
	const shown = ingredients.slice(0, MAX_INGREDIENTS);
	const more = ingredients.length - shown.length;

	return (
		<div className='meal-chip-tooltip' role='tooltip' style={style}>
			<div className='meal-chip-tooltip__name'>{mealName(meal, idea)}</div>
			{mealName(meal, idea) !== meal.name &&
			<div className='meal-chip-tooltip__notes'>{meal.name}</div>
			}
			{!!meal.notes &&
			<div className='meal-chip-tooltip__notes'>{meal.notes}</div>
			}
			{idea && !!idea.notes && idea.notes !== meal.notes &&
			<div className='meal-chip-tooltip__notes'>{idea.notes}</div>
			}
			{shown.length > 0 &&
			<div className='meal-chip-tooltip__ingredients'>
				{shown.map((ingredient) => ingredient.name).join(', ')}
				{more > 0 && `, and ${more} more`}
			</div>
			}
			{idea && !!idea.recipe_url &&
			<div className='meal-chip-tooltip__recipe'>Has a recipe link</div>
			}
		</div>
	);
}

/**
 * A grocery list pill's label, the list's own name when it has one beyond its day, and its first
 * items, one to a line. `groceryList` is a bought list as selectBoughtLists() gives it.
 */
export function GroceryListTooltip({ groceryList, rect }) {
	const label = groceryListLabel(groceryList);
	// The server names a bought list after its day, which the day's cell already says.
	const named = !!groceryList.purchased_on && !!groceryList.name &&
		groceryList.name !== moment(groceryList.purchased_on, 'YYYY-MM-DD').format('LL');
	const items = (groceryList.rows || []).filter((row) => row.name);
	const shown = items.slice(0, MAX_LIST_ITEMS);
	const more = items.length - shown.length;

	return (
		<div className='meal-chip-tooltip meal-chip-tooltip--grocery-list' role='tooltip' style={tipStyle(rect)}>
			<div className='meal-chip-tooltip__name'>{label}</div>
			{named &&
			<div className='meal-chip-tooltip__notes'>{groceryList.name}</div>
			}
			{shown.length > 0 &&
			<ul className='meal-chip-tooltip__items'>
				{shown.map((item, index) => <li key={item.id || index}>{item.name}</li>)}
				{more > 0 && <li className='meal-chip-tooltip__more'>and {more} more</li>}
			</ul>
			}
		</div>
	);
}
